import { useCallback, useEffect, useState } from 'react';
import { useAdminApiV1 } from '../../../clients/quipli-service/admin/admin-api-v1';
import { AdminLocationEmails } from '../../../clients/quipli-service/admin/admin';
import { debounce } from 'lodash';
import { toast } from '../../../util/toasts';
import { emailsKeys } from '../config/emails';

interface UseAdminEmailNotificationsProps {
  adminId?: number;
  adminLocations?: number[];
  fetchAtMount?: boolean;
}

export const useAdminEmailNotifications = ({
  adminId,
  adminLocations,
  fetchAtMount = true,
}: UseAdminEmailNotificationsProps) => {
  const { getUserAdminEmailNotifications, updateUserAdminEmailNotifications } = useAdminApiV1();

  const [isLoading, setIsLoading] = useState<boolean>(fetchAtMount);
  const [locationEmailSettings, setLocationEmailSettings] = useState<AdminLocationEmails>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Save admin email notification settings with debounce
  const saveEmailNotificationSettings = useCallback(
    debounce(async function (locationEmails: AdminLocationEmails, _adminId: number) {
      try {
        setIsSubmitting(true);
        await updateUserAdminEmailNotifications(_adminId!, locationEmails);
        toast('Email notifications updated successfully');
      } catch (e: any) {
        toast('Something wrong updating email notifications', 'error');
      } finally {
        setIsSubmitting(false);
      }
    }, 600),
    [],
  );

  // Update email notifications for a specific location
  const toggleEmailForLocation = useCallback(
    (emailType: string, locationId: number, isEnabled: boolean) => {
      setLocationEmailSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings };

        if (isEnabled) {
          if (!updatedSettings[locationId]) {
            updatedSettings[locationId] = [];
          }
          if (!updatedSettings[locationId].includes(emailType)) {
            updatedSettings[locationId] = [...updatedSettings[locationId], emailType];
          }
        } else {
          if (updatedSettings[locationId]) {
            updatedSettings[locationId] = updatedSettings[locationId].filter((item) => item !== emailType);
            // Remove location if no emails are selected
            if (updatedSettings[locationId].length === 0) {
              delete updatedSettings[locationId];
            }
          }
        }

        saveEmailNotificationSettings(updatedSettings, adminId!);
        return updatedSettings;
      });
    },
    [adminId],
  );

  // Select/deselect all email notifications for a specific location
  const toggleAllEmailsForLocation = useCallback(
    (locationId: number) => {
      setLocationEmailSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings };

        // Check if all emails are currently selected for this location
        const areAllCurrentlySelected =
          prevSettings[locationId] && emailsKeys.every((email) => prevSettings[locationId].includes(email));

        if (!areAllCurrentlySelected) {
          // Select all emails for this location
          updatedSettings[locationId] = [...emailsKeys];
        } else {
          // Deselect all emails for this location
          delete updatedSettings[locationId];
        }

        saveEmailNotificationSettings(updatedSettings, adminId!);
        return updatedSettings;
      });
    },
    [adminId],
  );

  // Check if all emails are selected for a specific location
  const areAllEmailsSelectedForLocation = useCallback(
    (locationId: number) => {
      if (!locationEmailSettings[locationId]) {
        return false;
      }

      return emailsKeys.every((email) => locationEmailSettings[locationId].includes(email));
    },
    [locationEmailSettings],
  );

  // Check if a specific email is selected for a location
  const isEmailSelectedForLocation = useCallback(
    (locationId: number, emailType: string) => {
      return locationEmailSettings[locationId]?.includes(emailType) || false;
    },
    [locationEmailSettings],
  );

  // Select/deselect all email notifications for all locations
  const toggleAllEmailsForAllLocations = useCallback(() => {
    if (!adminLocations || adminLocations.length === 0) return;

    setLocationEmailSettings((prevSettings) => {
      const updatedSettings = { ...prevSettings };

      // Check if all emails are currently selected for all locations
      const areAllCurrentlySelected = adminLocations.every(
        (locationId) =>
          prevSettings[locationId] && emailsKeys.every((email) => prevSettings[locationId].includes(email)),
      );

      if (!areAllCurrentlySelected) {
        // Select all emails for all locations
        adminLocations.forEach((locationId) => {
          updatedSettings[locationId] = [...emailsKeys];
        });
      } else {
        // Clear all selections
        Object.keys(updatedSettings).forEach((locationId) => {
          delete updatedSettings[parseInt(locationId)];
        });
      }

      saveEmailNotificationSettings(updatedSettings, adminId!);
      return updatedSettings;
    });
  }, [adminId, adminLocations]);

  // Remove all email notifications for a specific location
  const removeAllEmailsForLocation = useCallback(
    (locationId: number) => {
      setLocationEmailSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings };
        delete updatedSettings[locationId];
        saveEmailNotificationSettings(updatedSettings, adminId!);
        return updatedSettings;
      });
    },
    [adminId],
  );

  // Remove all email notifications for multiple locations
  const removeAllEmailsForLocations = useCallback(
    (locationIds: number[]) => {
      setLocationEmailSettings((prevSettings) => {
        const updatedSettings = { ...prevSettings };
        locationIds.forEach((locationId) => {
          delete updatedSettings[locationId];
        });
        saveEmailNotificationSettings(updatedSettings, adminId!);
        return updatedSettings;
      });
    },
    [adminId],
  );

  // Check if all emails are selected for all locations
  const areAllEmailsSelectedForAllLocations = useCallback(() => {
    if (!adminLocations || adminLocations.length === 0) return false;

    return adminLocations.every((locationId) => areAllEmailsSelectedForLocation(locationId));
  }, [adminLocations, areAllEmailsSelectedForLocation]);

  // Fetch admin email notifications
  const fetchEmailNotificationSettings = useCallback(async (adminId: number) => {
    try {
      const emailSettings = await getUserAdminEmailNotifications(adminId);
      setLocationEmailSettings(emailSettings);
    } catch (e: any) {
      toast('Error fetching email notifications', 'error');
    }
  }, []);

  // Initialize the hook
  const initializeEmailSettings = useCallback(async (adminId: number) => {
    setIsLoading(true);
    await fetchEmailNotificationSettings(adminId);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (fetchAtMount && adminId) {
      initializeEmailSettings(adminId);
    }
  }, [fetchAtMount, adminId]);

  return {
    // State
    isLoading,
    isSubmitting,
    locationEmailSettings,

    // Business logic
    toggleEmailForLocation,
    toggleAllEmailsForLocation,
    areAllEmailsSelectedForLocation,
    isEmailSelectedForLocation,
    toggleAllEmailsForAllLocations,
    areAllEmailsSelectedForAllLocations,
    removeAllEmailsForLocation,
    removeAllEmailsForLocations,
    saveEmailNotificationSettings,
    fetchEmailNotificationSettings,
  };
};
